import {weakMemo} from '@packages/utilities/memo';
import type {VariableMap} from '@packages/systems/core/utils/StyleVariables';
import type {
  LibraryManifest,
  LoadedLibraries,
} from '@packages/systems/libraries';
import {type PluginName, getPluginName} from '@packages/systems/wfdl-Qualified';

/**
 * Given a `VariableMap` and `LoadedLibraries`, returns an array of `LibraryManifest`
 * that have library variables.
 */
export const _getLibrariesWithVariables = weakMemo(
  (libraries: LoadedLibraries) =>
    weakMemo((variables: VariableMap): LibraryManifest<string>[] => {
      // 1️⃣ Extract unique pluginIds from variables' origin
      const pluginIdsFromVariables: Set<PluginName> = variables.reduce(
        (acc, variable) => {
          // If variable has an origin attribute, we know it's a library variable
          if (variable.origin) {
            // If so, add the pluginId to the set
            acc = acc.add(getPluginName(variable.origin));
          }
          return acc;
        },
        new Set<PluginName>()
      );

      const librariesWithVariables = Object.keys(libraries).reduce(
        (acc, pluginId) => {
          const manifest = libraries[pluginId];
          // 1️⃣ If the library pluginId is in the set of unique pluginIds from variables' origin and the library has a manifest
          if (manifest && pluginIdsFromVariables.has(pluginId)) {
            // 2️⃣ Add the library manifest to the array
            acc.push(manifest);
          }
          return acc;
        },
        [] as LibraryManifest<string>[]
      );

      return librariesWithVariables;
    })
);

/**
 * Serves as a wrapper function that calls `_getLibrariesWithVariables` in a
 * curried manner.
 */
export const getLibrariesWithVariables = (
  libraries: LoadedLibraries,
  variables: VariableMap
) => {
  return _getLibrariesWithVariables(libraries)(variables);
};
